// Port of Networking/NetworkError.swift.
//
// `NetworkError` is plain data (a `{ kind }` union) so it can be compared, logged and kept inside results;
// `NetworkFailure` is the Error that carries it when thrown. Every HTTPClient throws `NetworkFailure` only.

/** The only error an `HTTPClient` reports. Payloads are developer diagnostics, never UI text. */
export type NetworkError =
  /** The device is known to be offline (connectivity provider, forced offline mode), or a cache-only lookup made
   *  while offline found nothing. */
  | { kind: 'offline' }
  /** The request did not complete within its timeout. */
  | { kind: 'timeout' }
  /** The caller aborted the request (AbortSignal). Never retried. */
  | { kind: 'cancelled' }
  /** The URL is not an absolute `http`/`https` URL with a host. */
  | { kind: 'invalidURL' }
  /** The server answered with something that is not a readable HTTP response (opaque response, status 0). */
  | { kind: 'invalidResponse' }
  /** Non-2xx status. `retryAfter` is the parsed `Retry-After` header in seconds, if any. */
  | { kind: 'http'; status: number; retryAfter?: number }
  /** The body could not be decoded. */
  | { kind: 'decoding'; message: string }
  /** Any other transport-level failure (DNS, TLS, connection refused…). `code` is 0 for fetch failures. */
  | { kind: 'transport'; code: number; description: string };

/** Error object thrown by HTTP clients; `error` is the machine-readable reason. */
export class NetworkFailure extends Error {
  readonly error: NetworkError;

  constructor(error: NetworkError) {
    super(describeNetworkError(error));
    this.name = 'NetworkFailure';
    this.error = error;
  }
}

export function describeNetworkError(error: NetworkError): string {
  switch (error.kind) {
    case 'http':
      return error.retryAfter === undefined
        ? `http(${error.status})`
        : `http(${error.status}, retryAfter ${error.retryAfter} s)`;
    case 'decoding':
      return `decoding(${error.message})`;
    case 'transport':
      return `transport(${error.code}: ${error.description})`;
    default:
      return error.kind;
  }
}

/**
 * Whether repeating the same request later may succeed: timeouts, transport failures and HTTP 408 (Request Timeout),
 * 425 (Too Early), 429 (Too Many Requests) and 5xx.
 */
export function isRetryable(error: NetworkError): boolean {
  switch (error.kind) {
    case 'timeout':
    case 'transport':
      return true;
    case 'http':
      return (
        error.status === 408 ||
        error.status === 425 ||
        error.status === 429 ||
        (error.status >= 500 && error.status <= 599)
      );
    default:
      return false;
  }
}

/** The server-provided `Retry-After` delay (seconds), when the error is an HTTP error carrying one. */
export function retryAfterOf(error: NetworkError): number | undefined {
  return error.kind === 'http' ? error.retryAfter : undefined;
}

/** Equality of two errors (the Swift enum is `Equatable`). */
export function sameNetworkError(a: NetworkError, b: NetworkError): boolean {
  if (a.kind !== b.kind) return false;
  switch (a.kind) {
    case 'http': {
      const o = b as typeof a;
      return a.status === o.status && a.retryAfter === o.retryAfter;
    }
    case 'decoding':
      return a.message === (b as typeof a).message;
    case 'transport': {
      const o = b as typeof a;
      return a.code === o.code && a.description === o.description;
    }
    default:
      return true;
  }
}

function isAbortLike(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'AbortError';
}

function isTimeoutLike(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'TimeoutError';
}

/**
 * Maps anything thrown by `fetch`, a handler or a test double to a `NetworkError` (Swift `NetworkError.from`):
 * a `NetworkFailure` keeps its error; `AbortError` → cancelled; `TimeoutError` → timeout; anything else → transport.
 * `fetch` rejects with a bare `TypeError` for every network failure (DNS, offline, TLS, CORS) and gives no code, so
 * these are `transport(0, …)`; the HTTP client turns them into `offline` when the browser reports no network.
 */
export function networkErrorFrom(error: unknown): NetworkError {
  if (error instanceof NetworkFailure) return error.error;
  if (isAbortLike(error)) return { kind: 'cancelled' };
  if (isTimeoutLike(error)) return { kind: 'timeout' };
  if (error instanceof Error) return { kind: 'transport', code: 0, description: error.message };
  return { kind: 'transport', code: 0, description: String(error) };
}
